import { FC, useState, useRef } from 'react';
import { BvIcon } from 'bevi-icon';
import { ReactComponent as ThumbnailBar } from '../../../assets/img/thumbnail-bar.svg';
import { ChatAssistant } from '../../../models/Chat/ChatAssistantModel';
import './ChatSidebar.scss';

// Flag "Novo" estática por ícone (a tabela chats_assistants não tem esse campo).
const NEW_ASSISTANT_ICONS = ['moneyUp'];

interface ChatSidebarProps {
  assistants: ChatAssistant[];
  activeAssistantId: number | null;
  onAssistantSelect: (id: number | null) => void;
  onCollapsedChange: (collapsed: boolean) => void;
  messageCount?: number;
  isDisabled?: boolean;
  disabledTooltip?: string;
}

const ChatSidebar: FC<ChatSidebarProps> = ({
  assistants,
  activeAssistantId,
  onAssistantSelect,
  onCollapsedChange,
  isDisabled = false,
  disabledTooltip = "Complete o tutorial para acessar os assistentes",
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    onCollapsedChange(next);
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isDisabled) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({
      top: rect.top - 12,
      left: rect.left + rect.width / 2,
    });
    setTooltipVisible(true);
  };

  const handleMouseLeave = () => {
    setTooltipVisible(false);
  };

  return (
    <>
      <aside
        className={`chat-sidebar${collapsed ? ' chat-sidebar--collapsed' : ''}`}
        aria-label="Menu de funcionalidades"
      >
        <div className="chat-sidebar__header">
          {!collapsed && (
            <div className="chat-sidebar__header-info">
              <span className="chat-sidebar__header-title">Assistentes de IA</span>
            </div>
          )}
          <button
            className="chat-sidebar__toggle"
            onClick={handleToggle}
            title={collapsed ? 'Expandir menu' : 'Recolher menu'}
            aria-expanded={!collapsed}
          >
            <ThumbnailBar width={20} height={20} />
          </button>
        </div>

        <p className="chat-sidebar__subtitle">Escolha o assunto da sua dúvida para conversar com o agente especialista:</p>

        <nav className="chat-sidebar__nav">
          <ul className="chat-sidebar__list">
            {assistants.map((assistant) => {
              const isActive = activeAssistantId === assistant.id;
              const itemClass = [
                'chat-sidebar__item',
                isActive ? 'chat-sidebar__item--active' : '',
                isDisabled ? 'chat-sidebar__item--disabled' : '',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <li key={assistant.id}>
                  <button
                    className={itemClass}
                    onClick={() => !isDisabled && onAssistantSelect(activeAssistantId === assistant.id ? null : assistant.id)}
                    title={isDisabled ? '' : assistant.name}
                    aria-current={isActive ? 'page' : undefined}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <span className="chat-sidebar__icon-box" data-label={assistant.name}>
                      <BvIcon name={assistant.icon} size={1.6} variant="solid" />
                    </span>
                    {!collapsed && (
                      <span className="chat-sidebar__label">
                        {assistant.name}
                        {NEW_ASSISTANT_ICONS.includes(assistant.icon) && (
                          <em className="chat-sidebar__new">Novo</em>
                        )}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>

      {tooltipVisible && (
        <div
          className="chat-sidebar__tooltip"
          style={{
            position: 'fixed',
            top: `${tooltipPos.top}px`,
            left: `${tooltipPos.left}px`,
            transform: 'translateX(-50%)',
            zIndex: 9999,
          }}
        >
          {disabledTooltip}
        </div>
      )}
    </>
  );
};

export default ChatSidebar;
